import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Chip from "@mui/material/Chip";
import { RolesContext } from "../../context/User";
import { useErrValidate } from "../../utilitis/AuthorizationPage";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { blueGrey } from "@mui/material/colors";
import logger from "../../utilitis/logger";

const NursingHomeOrderForm = () => {
  const [stateroles, dispatchroles] = useContext(RolesContext);
  const { validationPage } = useErrValidate();
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [products, setProducts] = useState([]);
  const [deliveryDays, setDeliveryDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFreeQuantity, setIsFreeQuantity] = useState(false); // Customer can order any quantity
  const [reorderNotice, setReorderNotice] = useState(null); // Info message when "Order again" drops unavailable items

  // Sites state
  const [sites, setSites] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    reference: "",
    dueDate: "",
    dcode: "",
    notes: "",
    driverText: "",
    items: [],
    selectedFiles: [],
  });

  // File upload state
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Quantity validation error dialog
  const [quantityErrorDialog, setQuantityErrorDialog] = useState({
    open: false,
    itemIndex: null,
    partname: "",
    enteredQuantity: 0,
    packQuant: 0,
  });

  // User data from session
  const contactName = stateroles.name || "";
  const phoneNum = stateroles.phone || "";
  const custName = stateroles.userId || "";

  useEffect(() => {
    validationPage("nursinghome");
    fetchProducts();
    fetchDeliveryDays();
    fetchSites();
  }, [stateroles.userId]);

  // Restore form data when returning from confirmation page
  useEffect(() => {
    if (location.state?.formData) {
      setFormData(location.state.formData);
    }
  }, []);

  // "Order again" from history: pre-fill items once products are loaded, so we can
  // check which of the historical items are still available. Any item whose PARTNAME
  // no longer exists in the current product list is silently dropped.
  useEffect(() => {
    if (!location.state?.reorderItems || products.length === 0) {
      return;
    }

    const availablePartnames = new Set(products.map((p) => p.PARTNAME));
    const requestedItems = location.state.reorderItems;
    const validItems = requestedItems
      .filter((item) => availablePartnames.has(item.partname))
      .map((item) => ({ partname: item.partname, quantity: item.quantity }));
    const droppedItems = requestedItems.filter((item) => !availablePartnames.has(item.partname));

    setFormData((prev) => ({ ...prev, items: validItems }));

    if (droppedItems.length > 0) {
      const droppedNames = droppedItems.map((item) => item.productName || item.partname);
      setReorderNotice(
        droppedItems.length === 1
          ? `פריט ${droppedNames[0]} מההזמנה הקודמת כבר לא זמין והוסר מההזמנה החדשה.`
          : `הפריטים ${droppedNames.join(", ")} מההזמנה הקודמת כבר לא זמינים והוסרו מההזמנה החדשה.`,
      );
    }

    // Clear the router state so this doesn't re-apply (e.g. after a later products refetch).
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // Helper function to get next occurrence of a weekday
  const getNextWeekday = (weekdayNum, weeksAhead = 0) => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Convert Priority weekday (1=Sunday, 2=Monday) to JS (0=Sunday, 1=Monday)
    const targetDay = parseInt(weekdayNum) - 1;

    let daysUntilTarget = targetDay - currentDay;
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7; // Get next week's occurrence
    }

    // Add additional weeks
    daysUntilTarget += weeksAhead * 7;

    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilTarget);
    return nextDate;
  };

  // Generate delivery date options from backend data
  const generateDeliveryOptions = () => {
    // Backend now provides ready-to-use dates
    return deliveryDays.map((dateOption) => ({
      value: `${dateOption.date}T00:00:00+02:00`,
      label: dateOption.formatted,
      date: dateOption.date,
    }));
  };

  // Helper function to format date for Priority API
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}T00:00:00+02:00`;
  };

  // Helper function to format date for display (Hebrew)
  const formatDateForDisplay = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper function to get Hebrew day name
  const getHebrewDayName = (weekdayNum) => {
    const days = {
      1: "ראשון",
      2: "שני",
      3: "שלישי",
      4: "רביעי",
      5: "חמישי",
      6: "שישי",
      7: "שבת",
    };
    return days[weekdayNum] || "";
  };

  // File upload utility - Read file in chunks and convert to base64
  const CHUNK_SIZE = 1024 * 1024; // 1MB chunks

  const readFileInChunks = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const chunks = [];
      let offset = 0;

      const readNextChunk = () => {
        const chunk = file.slice(offset, offset + CHUNK_SIZE);
        reader.readAsDataURL(chunk);
      };

      reader.onload = (e) => {
        if (e.target.result) {
          // Extract base64 from DataURL (remove "data:mime/type;base64," prefix)
          const base64String = e.target.result.split(",")[1];
          chunks.push(base64String);
          offset += CHUNK_SIZE;

          if (offset < file.size) {
            // More chunks to read
            readNextChunk();
          } else {
            // Finished reading entire file
            resolve({
              name: file.name,
              type: file.type,
              size: file.size,
              chunks: chunks,
              totalChunks: chunks.length,
            });
          }
        }
      };

      reader.onerror = (error) => {
        logger.error("Error reading file:", error);
        reject(error);
      };

      readNextChunk();
    });
  };

  // Handle file selection and conversion
  const handleFileChange = async (event) => {
    try {
      setUploadingFiles(true);
      setError(null);

      const files = Array.from(event.target.files);

      if (files.length === 0) {
        setUploadingFiles(false);
        return;
      }

      // Read all files in chunks
      const fileReadPromises = files.map((file) => readFileInChunks(file));
      const fileDataArray = await Promise.all(fileReadPromises);

      // Add to existing files
      setFormData((prevFormData) => ({
        ...prevFormData,
        selectedFiles: [...prevFormData.selectedFiles, ...fileDataArray],
      }));

    } catch (error) {
      logger.error("Error reading files:", error);
      setError("שגיאה בקריאת הקבצים. נסה שוב.");
    } finally {
      setUploadingFiles(false);
      // Reset input to allow selecting the same file again
      event.target.value = null;
    }
  };

  // Remove file from list
  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter((_, i) => i !== index),
    }));
  };

  // Extract order data from PDF
  const handleExtractFromPdf = async (fileIndex, directFileData = null) => {
    try {
      setUploadingFiles(true);
      setError(null);

      // Use direct file data if provided, otherwise get from selectedFiles
      const file = directFileData || formData.selectedFiles[fileIndex];

      if (!file || file.type !== "application/pdf") {
        setError("ניתן לחלץ נתונים רק מקבצי PDF");
        setUploadingFiles(false);
        return;
      }

      // Combine chunks to get full base64 content
      const fullContent = Array.isArray(file.chunks)
        ? file.chunks.join("")
        : file.chunks;

      const response = await axios.post(
        "/nursinghome/extract-pdf",
        {
          pdfContent: fullContent,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data.success) {
        const extractedData = response.data.data;

        // Populate form with extracted data
        setFormData((prev) => ({
          ...prev,
          reference: extractedData.reference || prev.reference,
          notes: extractedData.notes || prev.notes,
          items: extractedData.items.map((item) => ({
            partname: item.partname,
            quantity: item.quantity,
            productName: item.productName || "",
          })),
        }));

        // Show success message
        let successMsg = `נתונים חולצו בהצלחה! ${extractedData.items.length} פריטים נמצאו.`;

        if (
          extractedData.notFoundItems &&
          extractedData.notFoundItems.length > 0
        ) {
          successMsg += ` \nשים לב: ${extractedData.notFoundItems.length} מק"טים לא נמצאו במערכת.`;
        }

        // Check if quantities were adjusted
        const adjustedItems = extractedData.items.filter(
          (item) => item.quantity !== item.originalQuantity,
        );
        if (adjustedItems.length > 0) {
          successMsg += `\n${adjustedItems.length} כמויות הותאמו למינימום הנדרש.`;
        }

        alert(successMsg);
      } else {
        setError(response.data.msg || "לא ניתן לחלץ נתונים מה-PDF");
      }
    } catch (err) {
      logger.error("Error extracting PDF:", err);
      if (err.response?.data?.canRetry) {
        setError("לא ניתן לחלץ נתונים מה-PDF. אנא מלא את הטופס ידנית.");
      } else if (err.response?.status === 403) {
        setError("אין הרשאה. אנא התחבר מחדש.");
      } else {
        setError(
          `שגיאה בחילוץ נתונים: ${err.response?.data?.msg || err.message}`,
        );
      }
    } finally {
      setUploadingFiles(false);
    }
  };

  // Fetch delivery sites from backend
  const fetchSites = async () => {
    try {
      const response = await axios.get("/nursinghome/sites", {
        withCredentials: true,
      });
      const fetchedSites = response.data?.data || [];
      setSites(fetchedSites);
    } catch (err) {
      logger.error("Error fetching sites:", err);
    }
  };

  // Fetch delivery days from backend
  const fetchDeliveryDays = async () => {
    try {
      const response = await axios.get("/nursinghome/delivery-days", {
        withCredentials: true,
      });

      if (response.data && response.data.data) {
        const availableDates = response.data.data;
        setDeliveryDays(availableDates);

        // Set first available date as default (only if not already set)
        if (availableDates.length > 0) {
          const firstDate = availableDates[0].date;
          const formattedDate = `${firstDate}T00:00:00+02:00`;
          setFormData((prev) => ({
            ...prev,
            dueDate: prev.dueDate || formattedDate,
          }));
        }
      }
    } catch (err) {
      logger.error("Error fetching delivery days:", err);
    }
  };

  // Fetch products from backend
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get("/nursinghome/products", {
        withCredentials: true,
      });

      if (response.data && response.data.data) {
        setProducts(response.data.data);
        // Set customer quantity restriction flag
        setIsFreeQuantity(response.data.isFreeQuantity || false);
      } else {
        setError("לא נמצאו מוצרים");
      }
    } catch (err) {
      logger.error("Error fetching products:", err);
      if (err.response?.status === 403) {
        setError("אין הרשאה לצפות במוצרים. אנא התחבר מחדש.");
      } else if (err.response?.status === 404) {
        setError("לא נמצאו מוצרים זמינים להזמנה.");
      } else if (err.response?.status >= 500) {
        setError("שגיאת שרת. אנא נסה שוב מאוחר יותר.");
      } else if (
        err.code === "ECONNABORTED" ||
        err.message.includes("timeout")
      ) {
        setError("תם הזמן הקצוב לטעינה. נסה שוב.");
      } else if (!navigator.onLine) {
        setError("אין חיבור לאינטרנט. אנא בדוק את החיבור שלך.");
      } else {
        setError(
          `שגיאה בטעינת המוצרים: ${
            err.response?.data?.msg || err.message || "נסה שוב מאוחר יותר"
          }`,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Add new item row
  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { partname: "", quantity: "" }], // Start with empty quantity until product is selected
    }));
  };

  // Remove item row
  const handleRemoveItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
    // Clear validation errors for this item
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`item_${index}_partname`];
      delete newErrors[`item_${index}_quantity`];
      return newErrors;
    });
  };

  // Handle item field changes
  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      // If product is being changed, set quantity based on customer type
      if (field === "partname" && value) {
        const selectedProduct = products.find((p) => p.PARTNAME === value);
        newItems[index].productName = selectedProduct?.PARTDES || "";
        if (isFreeQuantity) {
          // Free quantity customer - set to 1 or keep existing
          newItems[index].quantity = newItems[index].quantity || 1;
        } else {
          // Restricted customer - set to first valid option (1x PACKQUANT)
          const packQuant = getPackQuant(value);
          newItems[index].quantity = packQuant;
        }
      }

      return { ...prev, items: newItems };
    });
    // Clear validation error for this item field
    const errorKey = `item_${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Validate quantity when user finishes typing (onBlur)
  const handleQuantityBlur = (index) => {
    const item = formData.items[index];

    // Only validate for non-free customers (and not exempt products)
    const FREE_QUANTITY_PARTNAMES = ["202002400004"];
    if (!isFreeQuantity && item.partname && item.quantity && !FREE_QUANTITY_PARTNAMES.includes(item.partname)) {
      const packQuant = getPackQuant(item.partname);
      if (item.quantity > 0 && item.quantity % packQuant !== 0) {
        // Show error dialog
        setQuantityErrorDialog({
          open: true,
          itemIndex: index,
          partname: item.partname,
          enteredQuantity: item.quantity,
          packQuant: packQuant,
        });
      }
    }
  };

  // Get PACKQUANT for a product
  const getPackQuant = (partname) => {
    const product = products.find((p) => p.PARTNAME === partname);
    if (
      product &&
      product.PARTPACK_SUBFORM &&
      product.PARTPACK_SUBFORM.length > 0
    ) {
      return product.PARTPACK_SUBFORM[0].PACKQUANT || 1;
    }
    return 1; // Default if not found
  };

  // Check if quantity is a valid multiple of PACKQUANT
  const FREE_QUANTITY_PARTNAMES = ["202002400004"];
  const isValidQuantityMultiple = (partname, quantity) => {
    // Free quantity customers or exempt products can order any amount
    if (isFreeQuantity || FREE_QUANTITY_PARTNAMES.includes(partname)) {
      return true;
    }

    const packQuant = getPackQuant(partname);
    if (!quantity || quantity <= 0) {
      return false;
    }

    // Check if quantity is a multiple of packQuant
    return quantity % packQuant === 0;
  };

  // Generate quantity options based on PACKQUANT
  const generateQuantityOptions = (partname, maxMultiples = 50) => {
    const packQuant = getPackQuant(partname);
    const options = [];
    for (let i = 1; i <= maxMultiples; i++) {
      const totalUnits = i * packQuant;
      const cartonText = i === 1 ? "קרטון" : "קרטונים";
      options.push({
        value: totalUnits,
        label: `${totalUnits} יחידות (${i} ${cartonText} × ${packQuant})`,
      });
    }
    return options;
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = {};

    // אתר אספקה is optional - no validation needed

    // Reference is now optional - no validation needed

    // Validate at least one item
    if (formData.items.length === 0) {
      errors.items = "יש להוסיף לפחות פריט אחד";
    }

    // Validate each item
    const seenProducts = new Set();
    formData.items.forEach((item, index) => {
      if (!item.partname) {
        errors[`item_${index}_partname`] = "יש לבחור מוצר";
      } else if (seenProducts.has(item.partname)) {
        errors[`item_${index}_partname`] = "מוצר כבר קיים בהזמנה";
      } else {
        seenProducts.add(item.partname);
      }

      if (!item.quantity || item.quantity <= 0) {
        errors[`item_${index}_quantity`] = "כמות חייבת להיות גדולה מ-0";
      } else if (!isValidQuantityMultiple(item.partname, item.quantity)) {
        // Check if quantity is a valid multiple for restricted customers
        const packQuant = getPackQuant(item.partname);
        errors[`item_${index}_quantity`] =
          `הכמות חייבת להיות כפולה של ${packQuant} (מגש שלם)`;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission - navigate to confirmation page
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setError("יש לתקן את השגיאות בטופס");
      return;
    }

    navigate("/nursinghome/confirm", {
      state: {
        formData,
        contactName,
        phoneNum,
        products,
        deliveryDays,
        sites,
      },
    });
  };

  // Clear form
  const handleClearForm = () => {
    setFormData({
      reference: "",
      dueDate:
        deliveryDays.length > 0 ? `${deliveryDays[0].date}T00:00:00+02:00` : "",
      dcode: "",
      notes: "",
      driverText: "",
      items: [],
      selectedFiles: [],
    });
    setValidationErrors({});
    setError(null);
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error && products.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" sx={{ mt: 2 }} onClick={fetchProducts}>
            נסה שוב
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pb: 8 }}>
      <Box sx={{ mt: 4, mb: 4, minHeight: "100%", flex: 1 }}>
        {/* Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ShoppingCartIcon sx={{ fontSize: 40, color: "#1976d2" }} />
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  הזמנה חדשה - בית אבות
                </Typography>
              </Box>
              <Button
                variant="contained"
                component="label"
                startIcon={<AttachFileIcon />}
                disabled={uploadingFiles}
                color="primary"
              >
                {uploadingFiles ? "מעבד PDF..." : "העלה PDF להזמנה אוטומטית"}
                <input
                  type="file"
                  hidden
                  accept="application/pdf"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (file && file.type === "application/pdf") {
                      try {
                        const fileData = await readFileInChunks(file);

                        // Add file to selectedFiles list
                        setFormData((prevFormData) => ({
                          ...prevFormData,
                          selectedFiles: [
                            ...prevFormData.selectedFiles,
                            fileData,
                          ],
                        }));

                        // Automatically extract data from PDF
                        await handleExtractFromPdf(0, fileData);
                      } catch (error) {
                        logger.error("Error processing PDF:", error);
                        setError("שגיאה בעיבוד קובץ ה-PDF");
                      }
                    }
                    event.target.value = null;
                  }}
                />
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Reorder notice - shown when "Order again" dropped unavailable items */}
        {reorderNotice && (
          <Alert severity="info" sx={{ mb: 3 }} onClose={() => setReorderNotice(null)}>
            {reorderNotice}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Section 1: Contact Details (Read-only) */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                פרטי איש קשר
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="איש קשר"
                  value={contactName}
                  disabled
                  fullWidth
                />
                <TextField label="נייד" value={phoneNum} disabled fullWidth />
              </Box>
            </CardContent>
          </Card>

          {/* Section 2: Order Details */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                פרטי הזמנה
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="מס' הזמנת רכש "
                  value={formData.reference}
                  onChange={(e) =>
                    handleInputChange("reference", e.target.value)
                  }
                  error={!!validationErrors.reference}
                  helperText={validationErrors.reference}
                  fullWidth
                />

                {sites.length > 1 && (
                  <FormControl
                    fullWidth
                    error={!!validationErrors.dcode}
                  >
                    <InputLabel>אתר אספקה</InputLabel>
                    <Select
                      value={formData.dcode}
                      label="אתר אספקה"
                      onChange={(e) =>
                        handleInputChange("dcode", e.target.value)
                      }
                    >
                      <MenuItem value="">--</MenuItem>
                      {sites.map((site) => (
                        <MenuItem key={site.CODE} value={site.CODE} sx={{ direction: "ltr" }}>
                          {site.CODEDES}
                        </MenuItem>
                      ))}
                    </Select>
                    {validationErrors.dcode && (
                      <FormHelperText>{validationErrors.dcode}</FormHelperText>
                    )}
                  </FormControl>
                )}

                <FormControl fullWidth>
                  <InputLabel> צפי ליום הפצה *</InputLabel>
                  <Select
                    value={formData.dueDate}
                    label=" צפי ליום הפצה *"
                    onChange={(e) =>
                      handleInputChange("dueDate", e.target.value)
                    }
                    disabled={deliveryDays.length === 0}
                  >
                    {deliveryDays.length === 0 ? (
                      <MenuItem value="">טוען ימי חלוקה...</MenuItem>
                    ) : (
                      generateDeliveryOptions().map((option, index) => (
                        <MenuItem
                          key={`${option.date}-${index}`}
                          value={option.value}
                          sx={{ direction: "ltr" }}
                        >
                          {option.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <TextField
                  label="הערות"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                />

                <TextField
                  label="הוראות לנהג"
                  value={formData.driverText}
                  onChange={(e) =>
                    handleInputChange("driverText", e.target.value)
                  }
                  multiline
                  rows={3}
                  fullWidth
                />

                {/* File Upload Section */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFileIcon />}
                      disabled={uploadingFiles}
                    >
                      {uploadingFiles ? "מעלה קבצים..." : "צרף קבצים"}
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={handleFileChange}
                      />
                    </Button>
                    {uploadingFiles && <CircularProgress size={24} />}
                  </Box>

                  {formData.selectedFiles.length > 0 && (
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}
                    >
                      {formData.selectedFiles.map((file, index) => (
                        <Box
                          key={index}
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <Chip
                            label={`${file.name} (${(file.size / 1024).toFixed(
                              1,
                            )} KB)`}
                            onDelete={() => handleRemoveFile(index)}
                            color="primary"
                            variant="outlined"
                          />
                          {file.type === "application/pdf" && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleExtractFromPdf(index)}
                              disabled={uploadingFiles}
                              sx={{ minWidth: "auto", px: 2 }}
                            >
                              חלץ נתונים
                            </Button>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Section 3: Order Items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  פריטים להזמנה
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  size="small"
                >
                  הוסף פריט
                </Button>
              </Box>

              {formData.items.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  לקוח יקר – שים לב את הכמויות יש להזין ביחידות (מלבד הפריט אבאונד)
                </Alert>
              )}

              {validationErrors.items && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {validationErrors.items}
                </Alert>
              )}

              {formData.items.length > 0 && (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>מס'</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 400 }}>
                          מוצר *
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>כמות *</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>פעולות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Autocomplete
                              options={products}
                              freeSolo
                              getOptionLabel={(option) => {
                                // Handle both object and string (for freeSolo)
                                if (typeof option === "string") {
                                  return option;
                                }
                                return `${option.PARTNAME} - ${option.PARTDES}`;
                              }}
                              filterOptions={(options, { inputValue }) => {
                                if (!inputValue) return options;
                                const searchTerm = inputValue.toLowerCase();
                                return options.filter((option) => {
                                  const partname =
                                    option.PARTNAME?.toLowerCase() || "";
                                  const barcode =
                                    option.BARCODE?.toLowerCase() || "";
                                  const yarpapart =
                                    option.NEO_YARPAPART?.toLowerCase() || "";
                                  const partdes =
                                    option.PARTDES?.toLowerCase() || "";

                                  // Also check customer part names
                                  const custPartNames =
                                    option.NEO_CUSTPART_SUBFORM?.map(
                                      (cp) =>
                                        cp.CUSTPARTNAME?.toLowerCase() || "",
                                    ) || [];

                                  return (
                                    partname.includes(searchTerm) ||
                                    barcode.includes(searchTerm) ||
                                    yarpapart.includes(searchTerm) ||
                                    partdes.includes(searchTerm) ||
                                    custPartNames.some((cpn) =>
                                      cpn.includes(searchTerm),
                                    )
                                  );
                                });
                              }}
                              value={
                                products.find(
                                  (p) => p.PARTNAME === item.partname,
                                ) ||
                                item.partname ||
                                null
                              }
                              onChange={(event, newValue) => {
                                // Handle both object selection and free text
                                const partname =
                                  typeof newValue === "string"
                                    ? newValue
                                    : newValue?.PARTNAME || "";
                                handleItemChange(index, "partname", partname);
                              }}
                              onInputChange={(event, newInputValue, reason) => {
                                // Handle typing in the field
                                if (reason === "input") {
                                  handleItemChange(
                                    index,
                                    "partname",
                                    newInputValue,
                                  );
                                }
                              }}
                              renderOption={(props, option) => {
                                const packQuant =
                                  option.PARTPACK_SUBFORM?.[0]?.PACKQUANT ||
                                  null;
                                const custPartName =
                                  option.NEO_CUSTPART_SUBFORM?.[0]
                                    ?.CUSTPARTNAME || null;
                                return (
                                  <li {...props} key={option.PARTNAME}>
                                    <Box sx={{ width: "100%" }}>
                                      <Typography variant="body1">
                                        <strong>{option.PARTNAME}</strong>
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.primary"
                                        sx={{ mt: 0.5 }}
                                      >
                                        {option.PARTDES}
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 2,
                                          flexWrap: "wrap",
                                          mt: 0.5,
                                        }}
                                      >
                                        {option.BARCODE && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            ברקוד: {option.BARCODE}
                                          </Typography>
                                        )}
                                        {option.NEO_YARPAPART && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            מק"ט יצרן: {option.NEO_YARPAPART}
                                          </Typography>
                                        )}
                                        {custPartName && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            מק"ט ספק: {custPartName}
                                          </Typography>
                                        )}
                                        {packQuant && (
                                          <Typography
                                            variant="caption"
                                            color="primary"
                                            sx={{ fontWeight: 600 }}
                                          >
                                            כמות במגש שלם: {packQuant} יח
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </li>
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={
                                    !!validationErrors[`item_${index}_partname`]
                                  }
                                  helperText={
                                    validationErrors[
                                      `item_${index}_partname`
                                    ] ||
                                    'חיפוש לפי מק"ט, שם (English), ברקוד, מק"ט יצרן'
                                  }
                                  placeholder="חפש מוצר..."
                                />
                              )}
                              fullWidth
                            />
                            {(() => {
                              const selectedProduct = products.find(
                                (p) => p.PARTNAME === item.partname,
                              );
                              return (
                                selectedProduct && (
                                  <Typography
                                    variant="body2"
                                    dir="ltr"
                                    sx={{
                                      mt: 1,
                                      textAlign: "right",
                                      wordBreak: "break-word",
                                      unicodeBidi: "isolate",
                                    }}
                                  >
                                    {selectedProduct.PARTNAME} -{" "}
                                    {selectedProduct.PARTDES}
                                  </Typography>
                                )
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={item.quantity}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "quantity",
                                  parseInt(e.target.value) || 0,
                                )
                              }
                              onBlur={() => handleQuantityBlur(index)}
                              error={
                                !!validationErrors[`item_${index}_quantity`]
                              }
                              helperText={
                                validationErrors[`item_${index}_quantity`] ||
                                "כמות מוצרים להזמנה"
                              }
                              inputProps={{ min: 1 }}
                              sx={{ width: 300 }}
                              placeholder="כמות"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {formData.items.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  לא נוספו פריטים עדיין. לחץ על "הוסף פריט" להתחלה.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Form Footer */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button variant="outlined" onClick={handleClearForm}>
                ביטול
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={formData.items.length === 0}
                sx={{ minWidth: 120 }}
              >
               הצג הזמנה
              </Button>
            </Box>

            {/* Error message below buttons */}
            {error && !loading && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Box>
        </form>

        {/* Quantity Error Dialog */}
        <Dialog
          open={quantityErrorDialog.open}
          onClose={() =>
            setQuantityErrorDialog({ ...quantityErrorDialog, open: false })
          }
          maxWidth="sm"
          fullWidth        >
          <DialogTitle
            sx={{
              pb: 1,
              backgroundColor: "#fff3e0",
              color: "#e65100",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, direction: "ltr" }}>
              שים לב - הכמות לא תואמת למגש שלם
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ direction: "ltr", pt: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body1">
                הכמות שהזנת ({quantityErrorDialog.enteredQuantity}) אינה כפולה
                של הכמות המינימלית להזמנה.
              </Typography>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>כמות במגש שלם:</strong>{" "}
                  {quantityErrorDialog.packQuant} יחידות
                </Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary">
                אנא תקן את הכמות להזמנת מגשים שלמים בלבד.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              direction: "ltr",
              justifyContent: "flex-start",
              px: 3,
              pb: 2,
            }}
          >
            <Button
              onClick={() => {
                // Automatically fix to nearest multiple
                const { itemIndex, packQuant, enteredQuantity } =
                  quantityErrorDialog;
                const nearestMultiple =
                  Math.round(enteredQuantity / packQuant) * packQuant;
                const correctedQuantity =
                  nearestMultiple > 0 ? nearestMultiple : packQuant;

                handleItemChange(itemIndex, "quantity", correctedQuantity);
                setQuantityErrorDialog({ ...quantityErrorDialog, open: false });
              }}
              variant="contained"
              color="primary"
            >
              תקן אוטומטית לכמות הקרובה ביותר
            </Button>
            <Button
              onClick={() =>
                setQuantityErrorDialog({ ...quantityErrorDialog, open: false })
              }
              variant="outlined"
            >
              סגור ותקן ידנית
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default NursingHomeOrderForm;
